/**
 * Données fictives du mode démo. Une agence imaginaire « Sécurité Lumen »
 * (Lille) avec établissements, sites, clients, agents, contrats, planning,
 * pointages et facturation — tout se référence par des IDs canoniques pour
 * que les jointures inter-onglets fonctionnent.
 *
 * `matchFixture(pathWithQuery)` renvoie la donnée pour un GET donné (en
 * appliquant les filtres usuels : statut, from/to, :id), ou `undefined`
 * (→ la requête réelle passe, donc jamais de shape invalide).
 *
 * Les données sont volontairement NON typées (objets bruts) : elles sont
 * servies comme du JSON. Cela garantit que le build passe et qu'un éventuel
 * écart de champ dégrade au pire en cellule vide, jamais en crash.
 */
import { matchExtra } from "./fixtures.extra";

/* ---------------- IDs & constantes partagées ---------------- */
const TENANT = "demo-tenant-0000-0000-000000000000";
const TENANT_ID = TENANT;
const NOW = "2026-06-15T08:00:00.000Z";

const ETAB_SIEGE = "demo-etab-siege-0000-000000000001";
const ETAB_AGENCE = "demo-etab-agence-0000-00000000002";

const SITE1 = "demo-site-0000-0000-000000000001"; // gardiennage, Euralille
const SITE2 = "demo-site-0000-0000-000000000002"; // ssiap, Verlaine
const SITE3 = "demo-site-0000-0000-000000000003"; // evenementiel, Foire
const SITE4 = "demo-site-0000-0000-000000000004"; // telesurveillance, Lesquin

const CLI1 = "demo-cli-0000-0000-000000000001";
const CLI2 = "demo-cli-0000-0000-000000000002";
const CLI3 = "demo-cli-0000-0000-000000000003";
const CLI4 = "demo-cli-0000-0000-000000000004";

const CTR1 = "demo-ctr-0000-0000-000000000001";
const CTR2 = "demo-ctr-0000-0000-000000000002";
const CTR3 = "demo-ctr-0000-0000-000000000003";
const CTR4 = "demo-ctr-0000-0000-000000000004";

const AGT1 = "demo-agt-0000-0000-000000000001";
const AGT2 = "demo-agt-0000-0000-000000000002";
const AGT3 = "demo-agt-0000-0000-000000000003";
const AGT5 = "demo-agt-0000-0000-000000000005";
const AGT6 = "demo-agt-0000-0000-000000000006";

const CREATOR = "demo-usr-0000-0000-000000000001";

const EMETTEUR = {
  emetteurNom: "Sécurité Lumen SAS",
  emetteurAdresse: "14 rue de la Vigilance, 59000 Lille",
  emetteurSiret: "90412375600018",
  emetteurTvaIntracom: "FR42904123756",
  emetteurMentions:
    "SAS au capital de 30 000 € · RCS Lille Métropole 904 123 756",
  emetteurRib:
    "Banque Populaire du Nord · IBAN FR76 1350 7000 1234 5678 9012 345 · BIC CCBPFRPPLIL · Titulaire Sécurité Lumen SAS",
};

/* ---------------- Établissements ---------------- */
const ETABLISSEMENTS = [
  {
    id: ETAB_SIEGE,
    tenantId: TENANT,
    nom: "Sécurité Lumen — Siège (Lille)",
    adresse: "12 rue Nationale, 59000 Lille",
    estSiege: true,
    createdAt: "2025-09-01T09:00:00.000Z",
  },
  {
    id: ETAB_AGENCE,
    tenantId: TENANT,
    nom: "Agence Métropole Sud",
    adresse: "8 avenue de la République, 59650 Villeneuve-d'Ascq",
    estSiege: false,
    createdAt: "2026-01-12T09:00:00.000Z",
  },
];

/* ---------------- Sites ---------------- */
function site(partial: any) {
  return {
    tenantId: TENANT,
    etablissementId: ETAB_SIEGE,
    clientId: null,
    code: null,
    statut: "actif",
    adresse: null,
    codePostal: null,
    ville: null,
    pays: "FR",
    latitude: null,
    longitude: null,
    horairesOuverture: null,
    contactNom: null,
    contactPhone: null,
    contactEmail: null,
    consignesUrl: null,
    notes: null,
    createdAt: NOW,
    updatedAt: NOW,
    archivedAt: null,
    ...partial,
  };
}

const SITES = [
  site({
    id: SITE1,
    code: "EUR-01",
    nom: "Centre commercial Euralille",
    type: "gardiennage",
    clientId: CLI1,
    adresse: "Centre Euralille, 59777 Lille",
    codePostal: "59777",
    ville: "Lille",
    contactNom: "Mme Caroline Dubreuil",
    contactPhone: "03 20 14 55 80",
  }),
  site({
    id: SITE2,
    code: "SIEGE-VRL",
    nom: "Siège régional — Groupe Verlaine",
    type: "ssiap",
    clientId: CLI2,
    adresse: "40 quai du Wault, 59000 Lille",
    codePostal: "59000",
    ville: "Lille",
    etablissementId: ETAB_AGENCE,
    contactNom: "M. Hervé Lacombe",
    contactPhone: "03 20 30 11 22",
  }),
  site({
    id: SITE3,
    code: "EVT-FOIRE",
    nom: "Foire commerciale de Lille",
    type: "evenementiel",
    clientId: CLI3,
    adresse: "Grand Palais, 1 boulevard des Cités Unies, 59000 Lille",
    codePostal: "59000",
    ville: "Lille",
    contactNom: "Service événementiel",
    contactPhone: "03 20 00 00 00",
  }),
  site({
    id: SITE4,
    code: "TSV-NUIT",
    nom: "Plateau logistique — Lesquin",
    type: "telesurveillance",
    clientId: CLI4,
    adresse: "Zone CRT, 59810 Lesquin",
    codePostal: "59810",
    ville: "Lesquin",
    etablissementId: ETAB_AGENCE,
  }),
  site({
    id: "demo-site-0000-0000-000000000005",
    code: "K9-RONDE",
    nom: "Entrepôts Roubaix Est (rondes cynophiles)",
    type: "cynophile",
    adresse: "14 rue de l'Industrie, 59100 Roubaix",
    codePostal: "59100",
    ville: "Roubaix",
  }),
];

/* ---------------- Clients ---------------- */
const CLIENTS = [
  {
    id: CLI1,
    tenantId: TENANT,
    etablissementId: ETAB_SIEGE,
    raisonSociale: "Centre Commercial Euralille",
    formeJuridique: "SAS",
    siret: "39181527600025",
    tvaIntracom: "FR48391815276",
    codeNaf: "6820B",
    adresse: "100 Centre Commercial Euralille",
    codePostal: "59777",
    ville: "Lille",
    pays: "France",
    contactPrincipalNom: "Sophie Delcourt",
    contactPrincipalEmail: "s.delcourt@euralille-cc.fr",
    contactPrincipalPhone: "+33 3 20 14 52 00",
    contactPrincipalFonction: "Directrice de la sûreté",
    contactFacturationEmail: "comptabilite@euralille-cc.fr",
    conditionsPaiementJours: 45,
    iban: "FR7630004000031234567890143",
    bic: "BNPAFRPPXXX",
    statut: "actif",
    notes:
      "Prestation rondes + PC sécurité 24/7. Renfort obligatoire période soldes.",
    createdAt: "2026-01-12T09:30:00.000Z",
    updatedAt: "2026-06-03T14:20:00.000Z",
    archivedAt: null,
  },
  {
    id: CLI2,
    tenantId: TENANT,
    etablissementId: ETAB_SIEGE,
    raisonSociale: "Groupe Verlaine",
    formeJuridique: "SARL",
    siret: "52487961300018",
    tvaIntracom: "FR62524879613",
    codeNaf: "6831Z",
    adresse: "27 rue Nationale",
    codePostal: "59000",
    ville: "Lille",
    pays: "France",
    contactPrincipalNom: "Marc Verlaine",
    contactPrincipalEmail: "m.verlaine@groupe-verlaine.fr",
    contactPrincipalPhone: "+33 3 20 55 18 42",
    contactPrincipalFonction: "Responsable moyens généraux",
    contactFacturationEmail: null,
    conditionsPaiementJours: 30,
    iban: null,
    bic: null,
    statut: "actif",
    notes: null,
    createdAt: "2026-02-20T10:15:00.000Z",
    updatedAt: "2026-05-28T08:05:00.000Z",
    archivedAt: null,
  },
  {
    id: CLI3,
    tenantId: TENANT,
    etablissementId: ETAB_AGENCE,
    raisonSociale: "Foire Internationale de Lille",
    formeJuridique: "SA",
    siret: "30217845900034",
    tvaIntracom: "FR91302178459",
    codeNaf: "8230Z",
    adresse: "Grand Palais, Boulevard des Cités Unies",
    codePostal: "59000",
    ville: "Lille",
    pays: "France",
    contactPrincipalNom: "Nadia Bensaïd",
    contactPrincipalEmail: "n.bensaid@foiredelille.fr",
    contactPrincipalPhone: "+33 3 28 36 47 10",
    contactPrincipalFonction: "Chargée de production",
    contactFacturationEmail: "factures@foiredelille.fr",
    conditionsPaiementJours: 60,
    iban: "FR7610278073110002055370121",
    bic: "CMCIFR2A",
    statut: "actif",
    notes:
      "Prestation événementielle ponctuelle (foire de septembre). Pic d'effectif sur 12 jours.",
    createdAt: "2026-03-05T13:40:00.000Z",
    updatedAt: "2026-06-10T16:00:00.000Z",
    archivedAt: null,
  },
  {
    id: CLI4,
    tenantId: TENANT,
    etablissementId: ETAB_AGENCE,
    raisonSociale: "Plateau Logistique Lesquin",
    formeJuridique: "SAS",
    siret: "81034562700021",
    tvaIntracom: "FR30810345627",
    codeNaf: "5210B",
    adresse: "ZAC du Grand But, rue de l'Épinette",
    codePostal: "59810",
    ville: "Lesquin",
    pays: "France",
    contactPrincipalNom: "Julien Carpentier",
    contactPrincipalEmail: "j.carpentier@logistique-lesquin.fr",
    contactPrincipalPhone: "+33 3 20 87 33 90",
    contactPrincipalFonction: "Directeur de site",
    contactFacturationEmail: "compta@logistique-lesquin.fr",
    conditionsPaiementJours: 30,
    iban: null,
    bic: null,
    statut: "actif",
    notes: null,
    createdAt: "2025-11-18T07:50:00.000Z",
    updatedAt: "2026-06-12T09:25:00.000Z",
    archivedAt: null,
  },
];

/* ---------------- Agents ---------------- */
const AGENTS = [
  {
    id: AGT1,
    tenantId: TENANT_ID,
    etablissementId: ETAB_SIEGE,
    userId: null,
    matricule: "AGT-0001",
    civilite: "M",
    prenom: "Karim",
    nom: "Benali",
    nomUsage: null,
    dateNaissance: "1988-03-12",
    lieuNaissance: "Marseille",
    paysNaissance: "France",
    nationalite: "Française",
    email: "k.benali@securite-lumen.fr",
    telephone: "+33 6 11 22 33 44",
    telephoneUrgence: "+33 6 99 88 77 66",
    adresse: "14 rue des Oliviers",
    codePostal: "13008",
    ville: "Marseille",
    pays: "France",
    statut: "actif",
    typeContrat: "cdi",
    dateEntree: "2021-09-01",
    dateSortie: null,
    categorieCcn: "140",
    tempsTravail: "temps_plein",
    heuresHebdo: "35.00",
    specialites: ["gardiennage", "ssiap"],
    coutHoraireCharge: "22.5000",
    notes: "Chef d'équipe nuit. Permis B.",
    createdAt: "2021-09-01T08:00:00.000Z",
    updatedAt: "2026-06-02T09:14:00.000Z",
    archivedAt: null,
  },
  {
    id: AGT2,
    tenantId: TENANT_ID,
    etablissementId: ETAB_SIEGE,
    userId: null,
    matricule: "AGT-0002",
    civilite: "Mme",
    prenom: "Sophie",
    nom: "Lefèvre",
    nomUsage: null,
    dateNaissance: "1992-07-25",
    lieuNaissance: "Lyon",
    paysNaissance: "France",
    nationalite: "Française",
    email: "s.lefevre@securite-lumen.fr",
    telephone: "+33 6 22 33 44 55",
    telephoneUrgence: null,
    adresse: "8 avenue Jean Jaurès",
    codePostal: "69007",
    ville: "Lyon",
    pays: "France",
    statut: "actif",
    typeContrat: "cdi",
    dateEntree: "2022-01-15",
    dateSortie: null,
    categorieCcn: "150",
    tempsTravail: "temps_plein",
    heuresHebdo: "35.00",
    specialites: ["gardiennage", "evenementiel"],
    coutHoraireCharge: "21.0000",
    notes: null,
    createdAt: "2022-01-15T08:00:00.000Z",
    updatedAt: "2026-05-28T16:40:00.000Z",
    archivedAt: null,
  },
  {
    id: AGT3,
    tenantId: TENANT_ID,
    etablissementId: ETAB_AGENCE,
    userId: null,
    matricule: "AGT-0003",
    civilite: "M",
    prenom: "David",
    nom: "Nguyen",
    nomUsage: null,
    dateNaissance: "1990-11-03",
    lieuNaissance: "Paris",
    paysNaissance: "France",
    nationalite: "Française",
    email: "d.nguyen@securite-lumen.fr",
    telephone: "+33 6 33 44 55 66",
    telephoneUrgence: "+33 6 12 34 56 78",
    adresse: "22 boulevard de la Liberté",
    codePostal: "59000",
    ville: "Lille",
    pays: "France",
    statut: "actif",
    typeContrat: "cdi",
    dateEntree: "2020-06-01",
    dateSortie: null,
    categorieCcn: "160",
    tempsTravail: "temps_plein",
    heuresHebdo: "35.00",
    specialites: ["ssiap", "gardiennage"],
    coutHoraireCharge: "24.0000",
    notes: "SSIAP 2. Référent sécurité incendie agence.",
    createdAt: "2020-06-01T08:00:00.000Z",
    updatedAt: "2026-06-10T11:02:00.000Z",
    archivedAt: null,
  },
  {
    id: "demo-agt-0000-0000-000000000004",
    tenantId: TENANT_ID,
    etablissementId: ETAB_AGENCE,
    userId: null,
    matricule: "AGT-0004",
    civilite: "Mme",
    prenom: "Amel",
    nom: "Cherif",
    nomUsage: null,
    dateNaissance: "1995-02-18",
    lieuNaissance: "Toulouse",
    paysNaissance: "France",
    nationalite: "Française",
    email: "a.cherif@securite-lumen.fr",
    telephone: "+33 6 44 55 66 77",
    telephoneUrgence: null,
    adresse: "3 place du Capitole",
    codePostal: "31000",
    ville: "Toulouse",
    pays: "France",
    statut: "actif",
    typeContrat: "cdd",
    dateEntree: "2025-03-01",
    dateSortie: "2026-08-31",
    categorieCcn: "130",
    tempsTravail: "temps_partiel",
    heuresHebdo: "24.00",
    specialites: ["evenementiel"],
    coutHoraireCharge: "20.0000",
    notes: "Renfort saison événementielle.",
    createdAt: "2025-03-01T08:00:00.000Z",
    updatedAt: "2026-06-05T10:20:00.000Z",
    archivedAt: null,
  },
  {
    id: AGT5,
    tenantId: TENANT_ID,
    etablissementId: ETAB_AGENCE,
    userId: null,
    matricule: "AGT-0005",
    civilite: "M",
    prenom: "Pierre",
    nom: "Moreau",
    nomUsage: null,
    dateNaissance: "1985-09-09",
    lieuNaissance: "Rennes",
    paysNaissance: "France",
    nationalite: "Française",
    email: "p.moreau@securite-lumen.fr",
    telephone: "+33 6 55 66 77 88",
    telephoneUrgence: "+33 6 87 65 43 21",
    adresse: "11 rue de Bretagne",
    codePostal: "35000",
    ville: "Rennes",
    pays: "France",
    statut: "actif",
    typeContrat: "cdi",
    dateEntree: "2019-04-15",
    dateSortie: null,
    categorieCcn: "150",
    tempsTravail: "temps_plein",
    heuresHebdo: "35.00",
    specialites: ["cynophile", "gardiennage"],
    coutHoraireCharge: "25.5000",
    notes: "Maître-chien. Binôme canin « Rex ».",
    createdAt: "2019-04-15T08:00:00.000Z",
    updatedAt: "2026-06-12T08:45:00.000Z",
    archivedAt: null,
  },
  {
    id: AGT6,
    tenantId: TENANT_ID,
    etablissementId: ETAB_SIEGE,
    userId: null,
    matricule: "AGT-0006",
    civilite: "Mme",
    prenom: "Julie",
    nom: "Da Silva",
    nomUsage: null,
    dateNaissance: "1993-12-30",
    lieuNaissance: "Bordeaux",
    paysNaissance: "France",
    nationalite: "Française",
    email: "j.dasilva@securite-lumen.fr",
    telephone: "+33 6 66 77 88 99",
    telephoneUrgence: null,
    adresse: "5 cours de l'Intendance",
    codePostal: "33000",
    ville: "Bordeaux",
    pays: "France",
    statut: "actif",
    typeContrat: "interim",
    dateEntree: "2026-02-01",
    dateSortie: null,
    categorieCcn: "120",
    tempsTravail: "temps_plein",
    heuresHebdo: "35.00",
    specialites: ["gardiennage"],
    coutHoraireCharge: "19.5000",
    notes: null,
    createdAt: "2026-02-01T08:00:00.000Z",
    updatedAt: "2026-06-01T14:30:00.000Z",
    archivedAt: null,
  },
  {
    id: "demo-agt-0000-0000-000000000007",
    tenantId: TENANT_ID,
    etablissementId: ETAB_SIEGE,
    userId: null,
    matricule: "AGT-0007",
    civilite: "M",
    prenom: "Mehdi",
    nom: "Bouaziz",
    nomUsage: null,
    dateNaissance: "1987-05-20",
    lieuNaissance: "Nantes",
    paysNaissance: "France",
    nationalite: "Française",
    email: "m.bouaziz@securite-lumen.fr",
    telephone: "+33 6 77 88 99 00",
    telephoneUrgence: "+33 6 23 45 67 89",
    adresse: "30 rue de la Loire",
    codePostal: "44000",
    ville: "Nantes",
    pays: "France",
    statut: "suspendu",
    typeContrat: "cdi",
    dateEntree: "2018-10-01",
    dateSortie: null,
    categorieCcn: "150",
    tempsTravail: "temps_plein",
    heuresHebdo: "35.00",
    specialites: ["gardiennage", "intervention"],
    coutHoraireCharge: "23.0000",
    notes: "Suspendu : carte pro en cours de renouvellement.",
    createdAt: "2018-10-01T08:00:00.000Z",
    updatedAt: "2026-06-08T09:00:00.000Z",
    archivedAt: null,
  },
  {
    id: "demo-agt-0000-0000-000000000008",
    tenantId: TENANT_ID,
    etablissementId: ETAB_AGENCE,
    userId: null,
    matricule: "AGT-0008",
    civilite: "M",
    prenom: "Thomas",
    nom: "Rousseau",
    nomUsage: null,
    dateNaissance: "1983-08-14",
    lieuNaissance: "Strasbourg",
    paysNaissance: "France",
    nationalite: "Française",
    email: "t.rousseau@securite-lumen.fr",
    telephone: "+33 6 88 99 00 11",
    telephoneUrgence: null,
    adresse: "9 rue du Dôme",
    codePostal: "67000",
    ville: "Strasbourg",
    pays: "France",
    statut: "sorti",
    typeContrat: "cdd",
    dateEntree: "2023-05-01",
    dateSortie: "2026-04-30",
    categorieCcn: "130",
    tempsTravail: "temps_plein",
    heuresHebdo: "35.00",
    specialites: ["evenementiel", "gardiennage"],
    coutHoraireCharge: "20.5000",
    notes: "Fin de CDD au 30/04/2026.",
    createdAt: "2023-05-01T08:00:00.000Z",
    updatedAt: "2026-04-30T17:00:00.000Z",
    archivedAt: "2026-04-30T17:00:00.000Z",
  },
];

/* ---------------- Qualifications (par agent) ---------------- */
const QUALIFS: Record<string, any[]> = {
  [AGT1]: [
    {
      id: "demo-qual-0000-0000-000000000001",
      tenantId: TENANT_ID,
      agentId: AGT1,
      type: "carte_pro",
      numero: "CAR-013-2021-07-10-12345678",
      emisPar: "CNAPS",
      obtenuLe: "2021-07-10",
      expireLe: "2026-07-10",
      statut: "valide",
      documentId: null,
      notes: "Renouvellement à anticiper.",
      createdAt: "2021-07-10T08:00:00.000Z",
      updatedAt: "2026-06-02T09:14:00.000Z",
    },
    {
      id: "demo-qual-0000-0000-000000000002",
      tenantId: TENANT_ID,
      agentId: AGT1,
      type: "ssiap1",
      numero: null,
      emisPar: "GRETA Provence",
      obtenuLe: "2021-06-01",
      expireLe: "2027-06-01",
      statut: "valide",
      documentId: null,
      notes: null,
      createdAt: "2021-06-01T08:00:00.000Z",
      updatedAt: "2021-06-01T08:00:00.000Z",
    },
  ],
  [AGT2]: [
    {
      id: "demo-qual-0000-0000-000000000003",
      tenantId: TENANT_ID,
      agentId: AGT2,
      type: "carte_pro",
      numero: "CAR-069-2022-01-05-23456789",
      emisPar: "CNAPS",
      obtenuLe: "2022-01-05",
      expireLe: "2027-01-05",
      statut: "valide",
      documentId: null,
      notes: null,
      createdAt: "2022-01-05T08:00:00.000Z",
      updatedAt: "2022-01-05T08:00:00.000Z",
    },
    {
      id: "demo-qual-0000-0000-000000000004",
      tenantId: TENANT_ID,
      agentId: AGT2,
      type: "sst",
      numero: null,
      emisPar: "INRS",
      obtenuLe: "2024-09-15",
      expireLe: "2026-09-15",
      statut: "valide",
      documentId: null,
      notes: null,
      createdAt: "2024-09-15T08:00:00.000Z",
      updatedAt: "2024-09-15T08:00:00.000Z",
    },
  ],
  [AGT3]: [
    {
      id: "demo-qual-0000-0000-000000000005",
      tenantId: TENANT_ID,
      agentId: AGT3,
      type: "carte_pro",
      numero: "CAR-059-2022-05-20-34567890",
      emisPar: "CNAPS",
      obtenuLe: "2022-05-20",
      expireLe: "2027-05-20",
      statut: "valide",
      documentId: null,
      notes: null,
      createdAt: "2022-05-20T08:00:00.000Z",
      updatedAt: "2026-06-01T08:00:00.000Z",
    },
    {
      id: "demo-qual-0000-0000-000000000006",
      tenantId: TENANT_ID,
      agentId: AGT3,
      type: "ssiap2",
      numero: "SSIAP2-2021-0042",
      emisPar: "GRETA Hauts-de-France",
      obtenuLe: "2021-06-30",
      expireLe: "2026-06-30",
      statut: "valide",
      documentId: null,
      notes: "Recyclage triennal SSIAP 2 à planifier.",
      createdAt: "2021-06-30T08:00:00.000Z",
      updatedAt: "2026-06-10T11:02:00.000Z",
    },
  ],
  ["demo-agt-0000-0000-000000000004"]: [
    {
      id: "demo-qual-0000-0000-000000000007",
      tenantId: TENANT_ID,
      agentId: "demo-agt-0000-0000-000000000004",
      type: "carte_pro",
      numero: "CAR-031-2025-02-15-45678901",
      emisPar: "CNAPS",
      obtenuLe: "2025-02-15",
      expireLe: "2030-02-15",
      statut: "valide",
      documentId: null,
      notes: null,
      createdAt: "2025-02-15T08:00:00.000Z",
      updatedAt: "2025-02-15T08:00:00.000Z",
    },
  ],
  [AGT5]: [
    {
      id: "demo-qual-0000-0000-000000000008",
      tenantId: TENANT_ID,
      agentId: AGT5,
      type: "carte_pro",
      numero: "CAR-035-2024-04-10-56789012",
      emisPar: "CNAPS",
      obtenuLe: "2024-04-10",
      expireLe: "2029-04-10",
      statut: "valide",
      documentId: null,
      notes: null,
      createdAt: "2024-04-10T08:00:00.000Z",
      updatedAt: "2024-04-10T08:00:00.000Z",
    },
    {
      id: "demo-qual-0000-0000-000000000009",
      tenantId: TENANT_ID,
      agentId: AGT5,
      type: "cynophile",
      numero: "CYN-035-2024-0007",
      emisPar: "CNAPS",
      obtenuLe: "2024-04-10",
      expireLe: "2029-04-10",
      statut: "valide",
      documentId: null,
      notes: "Spécialité agent cynophile (binôme Rex).",
      createdAt: "2024-04-10T08:00:00.000Z",
      updatedAt: "2024-04-10T08:00:00.000Z",
    },
  ],
  [AGT6]: [
    {
      id: "demo-qual-0000-0000-000000000010",
      tenantId: TENANT_ID,
      agentId: AGT6,
      type: "carte_pro",
      numero: "CAR-033-2026-01-20-67890123",
      emisPar: "CNAPS",
      obtenuLe: "2026-01-20",
      expireLe: "2031-01-20",
      statut: "valide",
      documentId: null,
      notes: null,
      createdAt: "2026-01-20T08:00:00.000Z",
      updatedAt: "2026-01-20T08:00:00.000Z",
    },
  ],
  ["demo-agt-0000-0000-000000000007"]: [
    {
      id: "demo-qual-0000-0000-000000000011",
      tenantId: TENANT_ID,
      agentId: "demo-agt-0000-0000-000000000007",
      type: "carte_pro",
      numero: "CAR-044-2021-03-01-78901234",
      emisPar: "CNAPS",
      obtenuLe: "2021-03-01",
      expireLe: "2026-03-01",
      statut: "expire",
      documentId: null,
      notes: "Carte expirée, dossier de renouvellement en cours.",
      createdAt: "2021-03-01T08:00:00.000Z",
      updatedAt: "2026-06-08T09:00:00.000Z",
    },
  ],
  ["demo-agt-0000-0000-000000000008"]: [
    {
      id: "demo-qual-0000-0000-000000000012",
      tenantId: TENANT_ID,
      agentId: "demo-agt-0000-0000-000000000008",
      type: "carte_pro",
      numero: "CAR-067-2023-04-15-89012345",
      emisPar: "CNAPS",
      obtenuLe: "2023-04-15",
      expireLe: "2028-04-15",
      statut: "valide",
      documentId: null,
      notes: null,
      createdAt: "2023-04-15T08:00:00.000Z",
      updatedAt: "2023-04-15T08:00:00.000Z",
    },
  ],
};

/* ---------------- RIB agents ---------------- */
const RIBS: Record<string, any> = {
  [AGT1]: {
    ibanMasque: "FR76 **** **** **** **** **** 189",
    bic: "BNPAFRPP",
    titulaire: "Karim Benali",
    updatedAt: "2026-06-02T09:14:00.000Z",
  },
  [AGT2]: {
    ibanMasque: "FR14 **** **** **** **** **** 027",
    bic: "CEPAFRPP",
    titulaire: "Sophie Lefèvre",
    updatedAt: "2026-05-28T16:40:00.000Z",
  },
  [AGT3]: {
    ibanMasque: "FR62 **** **** **** **** **** 553",
    bic: "SOGEFRPP",
    titulaire: "David Nguyen",
    updatedAt: "2026-06-10T11:02:00.000Z",
  },
  [AGT5]: {
    ibanMasque: "FR30 **** **** **** **** **** 741",
    bic: "CMCIFRPP",
    titulaire: "Pierre Moreau",
    updatedAt: "2026-06-12T08:45:00.000Z",
  },
};

/* ---------------- Contrats ---------------- */
const CONTRATS = [
  {
    id: CTR1,
    tenantId: TENANT,
    clientId: CLI1,
    siteId: SITE1,
    numero: "CTR-2026-0001",
    dateDebut: "2026-01-01",
    dateFin: null,
    tarifJourOuvre: "20.50",
    tarifNuit: "25.00",
    tarifSamedi: "23.00",
    tarifDimanche: "30.75",
    tarifFerie: "41.00",
    tvaPct: "20.00",
    autoliquidationTva: false,
    franchiseHeures: null,
    clauses:
      "Gardiennage filtrage des accès et rondes du centre commercial. Préavis de résiliation 3 mois. Indexation annuelle des taux sur l'indice Syntec au 1er janvier.",
    documentId: null,
    statut: "actif",
    createdAt: "2025-12-15T09:30:00.000Z",
    updatedAt: "2025-12-20T14:05:00.000Z",
    archivedAt: null,
  },
  {
    id: CTR2,
    tenantId: TENANT,
    clientId: CLI2,
    siteId: SITE2,
    numero: "CTR-2026-0002",
    dateDebut: "2026-02-01",
    dateFin: null,
    tarifJourOuvre: "21.50",
    tarifNuit: "26.50",
    tarifSamedi: "24.25",
    tarifDimanche: "32.25",
    tarifFerie: "43.00",
    tvaPct: "20.00",
    autoliquidationTva: false,
    franchiseHeures: null,
    clauses:
      "Service de sécurité incendie SSIAP poste central de sécurité 24h/24. Effectif minimum 1 SSIAP 1 par vacation. Astreinte chef d'équipe SSIAP 2 sur appel.",
    documentId: null,
    statut: "actif",
    createdAt: "2026-01-18T10:12:00.000Z",
    updatedAt: "2026-01-25T16:40:00.000Z",
    archivedAt: null,
  },
  {
    id: CTR3,
    tenantId: TENANT,
    clientId: CLI3,
    siteId: SITE3,
    numero: "CTR-2026-0003",
    dateDebut: "2026-04-15",
    dateFin: "2026-09-30",
    tarifJourOuvre: "22.00",
    tarifNuit: "27.50",
    tarifSamedi: "25.00",
    tarifDimanche: "33.00",
    tarifFerie: "44.00",
    tvaPct: "20.00",
    autoliquidationTva: false,
    franchiseHeures: null,
    clauses:
      "Mission événementielle Foire de Lille. Contrôle d'accès, palpation de sécurité et gestion de flux sur la durée du salon. Renfort week-end et jours d'affluence. Contrat à durée déterminée.",
    documentId: null,
    statut: "actif",
    createdAt: "2026-03-02T08:45:00.000Z",
    updatedAt: "2026-03-10T11:20:00.000Z",
    archivedAt: null,
  },
  {
    id: CTR4,
    tenantId: TENANT,
    clientId: CLI4,
    siteId: SITE4,
    numero: "CTR-2026-0004",
    dateDebut: "2026-03-01",
    dateFin: null,
    tarifJourOuvre: "19.50",
    tarifNuit: "24.00",
    tarifSamedi: "22.00",
    tarifDimanche: "29.25",
    tarifFerie: "39.00",
    tvaPct: "20.00",
    autoliquidationTva: false,
    franchiseHeures: "20.00",
    clauses:
      "Télésurveillance du plateau logistique avec levée de doute vidéo et intervention sur alarme. Forfait mensuel incluant 20 heures d'intervention, heures supplémentaires facturées au taux jour ouvré. Astreinte 24h/24 7j/7.",
    documentId: null,
    statut: "actif",
    createdAt: "2026-02-10T13:00:00.000Z",
    updatedAt: "2026-02-22T09:15:00.000Z",
    archivedAt: null,
  },
];

/* ---------------- Vacations (semaine 15→21 juin 2026) ---------------- */
function vac(p: any) {
  return {
    tenantId: TENANT,
    agentId: null,
    specialiteRequise: null,
    consignes: null,
    prixFacture: null,
    coutCharge: null,
    notes: null,
    createdById: CREATOR,
    createdAt: "2026-06-08T09:00:00.000Z",
    updatedAt: "2026-06-12T11:00:00.000Z",
    cancelledAt: null,
    ...p,
  };
}

const VACATIONS = [
  vac({ id: "demo-vac-0000-0000-000000000001", contratId: CTR1, siteId: SITE1, agentId: AGT6, debut: "2026-06-15T05:00:00.000Z", fin: "2026-06-15T17:00:00.000Z", dureeHeures: "12", typePeriode: "jour_ouvre", posteLibelle: "Accueil & filtrage Euralille", specialiteRequise: "gardiennage", statut: "terminee", prixFacture: "294.00", coutCharge: "210.00", updatedAt: "2026-06-15T17:05:00.000Z" }),
  vac({ id: "demo-vac-0000-0000-000000000002", contratId: CTR2, siteId: SITE2, agentId: AGT3, debut: "2026-06-15T06:00:00.000Z", fin: "2026-06-15T16:00:00.000Z", dureeHeures: "10", typePeriode: "jour_ouvre", posteLibelle: "Poste SSIAP — Tour Verlaine", specialiteRequise: "ssiap", statut: "terminee", prixFacture: "265.00", coutCharge: "190.00", updatedAt: "2026-06-15T16:04:00.000Z" }),
  vac({ id: "demo-vac-0000-0000-000000000003", contratId: CTR1, siteId: SITE1, agentId: AGT5, debut: "2026-06-15T19:00:00.000Z", fin: "2026-06-16T05:00:00.000Z", dureeHeures: "10", typePeriode: "nuit", posteLibelle: "Ronde cynophile nocturne", specialiteRequise: "cynophile", statut: "terminee", prixFacture: "320.00", coutCharge: "230.00", updatedAt: "2026-06-16T05:03:00.000Z" }),
  vac({ id: "demo-vac-0000-0000-000000000004", contratId: CTR1, siteId: SITE1, agentId: AGT1, debut: "2026-06-16T05:00:00.000Z", fin: "2026-06-16T17:00:00.000Z", dureeHeures: "12", typePeriode: "jour_ouvre", posteLibelle: "Accueil & filtrage Euralille", specialiteRequise: "gardiennage", statut: "terminee", prixFacture: "294.00", coutCharge: "210.00", updatedAt: "2026-06-16T17:06:00.000Z" }),
  vac({ id: "demo-vac-0000-0000-000000000005", contratId: CTR4, siteId: SITE4, agentId: AGT6, debut: "2026-06-16T06:00:00.000Z", fin: "2026-06-16T14:00:00.000Z", dureeHeures: "8", typePeriode: "jour_ouvre", posteLibelle: "Opérateur télésurveillance Lesquin", specialiteRequise: "telesurveillance", statut: "terminee", prixFacture: "212.00", coutCharge: "152.00", updatedAt: "2026-06-16T14:02:00.000Z" }),
  vac({ id: "demo-vac-0000-0000-000000000006", contratId: CTR2, siteId: SITE2, agentId: AGT1, debut: "2026-06-17T06:00:00.000Z", fin: "2026-06-17T16:00:00.000Z", dureeHeures: "10", typePeriode: "jour_ouvre", posteLibelle: "Poste SSIAP — Tour Verlaine", specialiteRequise: "ssiap", statut: "terminee", prixFacture: "265.00", coutCharge: "190.00", updatedAt: "2026-06-17T16:05:00.000Z" }),
  vac({ id: "demo-vac-0000-0000-000000000007", contratId: CTR1, siteId: SITE1, agentId: AGT5, debut: "2026-06-17T19:00:00.000Z", fin: "2026-06-18T05:00:00.000Z", dureeHeures: "10", typePeriode: "nuit", posteLibelle: "Gardiennage nuit Euralille", specialiteRequise: "gardiennage", statut: "terminee", prixFacture: "300.00", coutCharge: "215.00", updatedAt: "2026-06-18T05:04:00.000Z" }),
  vac({ id: "demo-vac-0000-0000-000000000008", contratId: CTR1, siteId: SITE1, agentId: AGT6, debut: "2026-06-18T05:00:00.000Z", fin: "2026-06-18T17:00:00.000Z", dureeHeures: "12", typePeriode: "jour_ouvre", posteLibelle: "Accueil & filtrage Euralille", specialiteRequise: "gardiennage", statut: "affectee", prixFacture: "294.00", coutCharge: "210.00" }),
  vac({ id: "demo-vac-0000-0000-000000000009", contratId: CTR4, siteId: SITE4, agentId: AGT3, debut: "2026-06-18T06:00:00.000Z", fin: "2026-06-18T14:00:00.000Z", dureeHeures: "8", typePeriode: "jour_ouvre", posteLibelle: "Opérateur télésurveillance Lesquin", specialiteRequise: "telesurveillance", statut: "affectee", prixFacture: "212.00", coutCharge: "152.00" }),
  vac({ id: "demo-vac-0000-0000-000000000010", contratId: CTR2, siteId: SITE2, agentId: AGT3, debut: "2026-06-18T19:00:00.000Z", fin: "2026-06-19T05:00:00.000Z", dureeHeures: "10", typePeriode: "nuit", posteLibelle: "Poste SSIAP nuit — Tour Verlaine", specialiteRequise: "ssiap", statut: "affectee", prixFacture: "320.00", coutCharge: "230.00" }),
  vac({ id: "demo-vac-0000-0000-000000000011", contratId: CTR1, siteId: SITE1, agentId: AGT1, debut: "2026-06-19T05:00:00.000Z", fin: "2026-06-19T17:00:00.000Z", dureeHeures: "12", typePeriode: "jour_ouvre", posteLibelle: "Accueil & filtrage Euralille", specialiteRequise: "gardiennage", statut: "affectee", prixFacture: "294.00", coutCharge: "210.00" }),
  vac({ id: "demo-vac-0000-0000-000000000012", contratId: CTR3, siteId: SITE3, agentId: AGT2, debut: "2026-06-20T08:00:00.000Z", fin: "2026-06-20T20:00:00.000Z", dureeHeures: "12", typePeriode: "samedi", posteLibelle: "Contrôle d'accès Foire de Lille", specialiteRequise: "evenementiel", statut: "affectee", prixFacture: "360.00", coutCharge: "258.00" }),
  vac({ id: "demo-vac-0000-0000-000000000013", contratId: CTR3, siteId: SITE3, agentId: null, debut: "2026-06-20T08:00:00.000Z", fin: "2026-06-20T20:00:00.000Z", dureeHeures: "12", typePeriode: "samedi", posteLibelle: "Renfort sécurité Foire — poste 2", specialiteRequise: "evenementiel", statut: "pourvoir", prixFacture: "360.00", coutCharge: "258.00", notes: "Poste non couvert — relancer le vivier événementiel.", updatedAt: "2026-06-08T09:36:00.000Z" }),
  vac({ id: "demo-vac-0000-0000-000000000014", contratId: CTR3, siteId: SITE3, agentId: AGT2, debut: "2026-06-21T08:00:00.000Z", fin: "2026-06-21T18:00:00.000Z", dureeHeures: "10", typePeriode: "dimanche", posteLibelle: "Contrôle d'accès Foire de Lille", specialiteRequise: "evenementiel", statut: "affectee", prixFacture: "350.00", coutCharge: "250.00" }),
];

/* ---------------- Pointages ---------------- */
function ptg(p: any) {
  return {
    pointeeClientAt: null,
    latitude: null,
    longitude: null,
    precisionMetres: null,
    siteLatitude: null,
    siteLongitude: null,
    ecartMetres: null,
    methode: "gps",
    horsCreneau: false,
    commentaire: null,
    photoDocumentId: null,
    ...p,
    createdAt: p.pointeeAt,
  };
}

const POINTAGES = [
  ptg({ id: "demo-ptg-0000-0000-000000000001", vacationId: "demo-vac-0000-0000-000000000001", agentId: AGT6, kind: "prise_poste", pointeeAt: "2026-06-15T04:57:00.000Z", pointeeClientAt: "2026-06-15T04:57:00.000Z", latitude: 50.6383, longitude: 3.0758, precisionMetres: 12, siteLatitude: 50.638, siteLongitude: 3.0755, ecartMetres: 28 }),
  ptg({ id: "demo-ptg-0000-0000-000000000002", vacationId: "demo-vac-0000-0000-000000000001", agentId: AGT6, kind: "sortie_poste", pointeeAt: "2026-06-15T17:03:00.000Z", pointeeClientAt: "2026-06-15T17:03:00.000Z", latitude: 50.6381, longitude: 3.0757, precisionMetres: 14, siteLatitude: 50.638, siteLongitude: 3.0755, ecartMetres: 24 }),
  ptg({ id: "demo-ptg-0000-0000-000000000003", vacationId: "demo-vac-0000-0000-000000000002", agentId: AGT3, kind: "prise_poste", pointeeAt: "2026-06-15T06:42:00.000Z", pointeeClientAt: "2026-06-15T06:42:00.000Z", latitude: 50.6332, longitude: 3.0731, precisionMetres: 18, siteLatitude: 50.633, siteLongitude: 3.073, ecartMetres: 23, horsCreneau: true, commentaire: "Retard transport en commun." }),
  ptg({ id: "demo-ptg-0000-0000-000000000004", vacationId: "demo-vac-0000-0000-000000000002", agentId: AGT3, kind: "sortie_poste", pointeeAt: "2026-06-15T16:01:00.000Z", pointeeClientAt: "2026-06-15T16:01:00.000Z", latitude: 50.6331, longitude: 3.073, precisionMetres: 16, siteLatitude: 50.633, siteLongitude: 3.073, ecartMetres: 11 }),
  ptg({ id: "demo-ptg-0000-0000-000000000005", vacationId: "demo-vac-0000-0000-000000000003", agentId: AGT5, kind: "prise_poste", pointeeAt: "2026-06-15T18:55:00.000Z", pointeeClientAt: "2026-06-15T18:55:00.000Z", latitude: 50.6382, longitude: 3.0759, precisionMetres: 10, siteLatitude: 50.638, siteLongitude: 3.0755, ecartMetres: 32 }),
  ptg({ id: "demo-ptg-0000-0000-000000000006", vacationId: "demo-vac-0000-0000-000000000003", agentId: AGT5, kind: "sortie_poste", pointeeAt: "2026-06-16T05:02:00.000Z", pointeeClientAt: "2026-06-16T05:02:00.000Z", latitude: 50.638, longitude: 3.0756, precisionMetres: 13, siteLatitude: 50.638, siteLongitude: 3.0755, ecartMetres: 9 }),
  ptg({ id: "demo-ptg-0000-0000-000000000007", vacationId: "demo-vac-0000-0000-000000000004", agentId: AGT1, kind: "prise_poste", pointeeAt: "2026-06-16T05:01:00.000Z", pointeeClientAt: "2026-06-16T05:01:00.000Z", siteLatitude: 50.638, siteLongitude: 3.0755, methode: "qr_code" }),
  ptg({ id: "demo-ptg-0000-0000-000000000008", vacationId: "demo-vac-0000-0000-000000000004", agentId: AGT1, kind: "sortie_poste", pointeeAt: "2026-06-16T17:04:00.000Z", pointeeClientAt: "2026-06-16T17:04:00.000Z", siteLatitude: 50.638, siteLongitude: 3.0755, methode: "qr_code" }),
  ptg({ id: "demo-ptg-0000-0000-000000000009", vacationId: "demo-vac-0000-0000-000000000005", agentId: AGT6, kind: "prise_poste", pointeeAt: "2026-06-16T05:58:00.000Z", pointeeClientAt: "2026-06-16T05:58:00.000Z", latitude: 50.5861, longitude: 3.1082, precisionMetres: 15, siteLatitude: 50.586, siteLongitude: 3.108, ecartMetres: 19 }),
  ptg({ id: "demo-ptg-0000-0000-000000000010", vacationId: "demo-vac-0000-0000-000000000005", agentId: AGT6, kind: "sortie_poste", pointeeAt: "2026-06-16T13:20:00.000Z", pointeeClientAt: "2026-06-16T13:20:00.000Z", latitude: 50.586, longitude: 3.1081, precisionMetres: 17, siteLatitude: 50.586, siteLongitude: 3.108, ecartMetres: 8, horsCreneau: true, commentaire: "Relève anticipée par opérateur suivant." }),
  ptg({ id: "demo-ptg-0000-0000-000000000011", vacationId: "demo-vac-0000-0000-000000000006", agentId: AGT1, kind: "prise_poste", pointeeAt: "2026-06-17T05:59:00.000Z", pointeeClientAt: "2026-06-17T05:59:00.000Z", latitude: 50.6331, longitude: 3.0731, precisionMetres: 11, siteLatitude: 50.633, siteLongitude: 3.073, ecartMetres: 14 }),
  ptg({ id: "demo-ptg-0000-0000-000000000012", vacationId: "demo-vac-0000-0000-000000000006", agentId: AGT1, kind: "sortie_poste", pointeeAt: "2026-06-17T16:02:00.000Z", pointeeClientAt: "2026-06-17T16:02:00.000Z", latitude: 50.633, longitude: 3.073, precisionMetres: 12, siteLatitude: 50.633, siteLongitude: 3.073, ecartMetres: 6 }),
  ptg({ id: "demo-ptg-0000-0000-000000000013", vacationId: "demo-vac-0000-0000-000000000007", agentId: AGT5, kind: "prise_poste", pointeeAt: "2026-06-17T18:58:00.000Z", pointeeClientAt: "2026-06-17T18:58:00.000Z", latitude: 50.6383, longitude: 3.0756, precisionMetres: 13, siteLatitude: 50.638, siteLongitude: 3.0755, ecartMetres: 33 }),
  ptg({ id: "demo-ptg-0000-0000-000000000014", vacationId: "demo-vac-0000-0000-000000000007", agentId: AGT5, kind: "sortie_poste", pointeeAt: "2026-06-18T05:05:00.000Z", siteLatitude: 50.638, siteLongitude: 3.0755, methode: "manuel", commentaire: "Pointage manuel — GPS device indisponible." }),
  ptg({ id: "demo-ptg-0000-0000-000000000015", vacationId: "demo-vac-0000-0000-000000000008", agentId: AGT6, kind: "prise_poste", pointeeAt: "2026-06-18T04:56:00.000Z", pointeeClientAt: "2026-06-18T04:56:00.000Z", latitude: 50.6382, longitude: 3.0756, precisionMetres: 10, siteLatitude: 50.638, siteLongitude: 3.0755, ecartMetres: 21 }),
  ptg({ id: "demo-ptg-0000-0000-000000000016", vacationId: "demo-vac-0000-0000-000000000009", agentId: AGT3, kind: "prise_poste", pointeeAt: "2026-06-18T06:03:00.000Z", pointeeClientAt: "2026-06-18T06:03:00.000Z", latitude: 50.5862, longitude: 3.1079, precisionMetres: 16, siteLatitude: 50.586, siteLongitude: 3.108, ecartMetres: 24 }),
];

/* ---------------- Devis ---------------- */
const DEVIS = [
  {
    id: "demo-dev-0000-0000-000000000001", numero: null, annee: null, mois: null, sequenceNum: null,
    clientId: CLI4, siteId: SITE4,
    objet: "Gardiennage poste fixe nuit · Plateau Logistique Lesquin · 12 mois",
    clientRaisonSociale: "Plateau Logistique Lesquin", clientFormeJuridique: "SAS", clientSiret: "81034562700021",
    clientTvaIntracom: "FR30810345627", clientAdresse: "ZAC du Grand But, rue de l'Épinette", clientCodePostal: "59810",
    clientVille: "Lesquin", clientPays: "France", clientContactNom: "Julien Carpentier", clientContactEmail: "j.carpentier@logistique-lesquin.fr",
    emetteurNom: null, emetteurAdresse: null, emetteurSiret: null, emetteurTvaIntracom: null, emetteurRib: null,
    totalHt: 9576.0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 1915.2, totalTtc: 11491.2,
    statut: "brouillon", publicToken: null, validiteJours: 30, dateValidite: null, dateEmission: null,
    envoyeAt: null, envoyeById: null, accepteAt: null, accepteIp: null, accepteParNom: null,
    refuseAt: null, refuseMotif: null, annuleAt: null, annuleMotif: null, contratId: null,
    notesInternes: "Tarif aligné sur la grille agent SSIAP 1 + majoration nuit.",
    conditionsParticulieres: "Renouvellement tacite par période de 12 mois · préavis 3 mois.",
    pdfStorageKey: null, createdAt: "2026-06-16T09:12:00.000Z", updatedAt: "2026-06-16T09:12:00.000Z",
    lignes: [
      { id: "demo-dl-0000-0000-000000000001", devisId: "demo-dev-0000-0000-000000000001", description: "Agent de sécurité - vacation nuit (21h-6h)", typePeriode: "nuit", qte: 252, unite: "h", prixUnitaire: 22.5, prixHt: 5670.0, ordre: 0 },
      { id: "demo-dl-0000-0000-000000000002", devisId: "demo-dev-0000-0000-000000000001", description: "Agent de sécurité - vacation jour ouvré", typePeriode: "jour_ouvre", qte: 195, unite: "h", prixUnitaire: 20.03, prixHt: 3905.85, ordre: 1 },
    ],
  },
  {
    id: "demo-dev-0000-0000-000000000002", numero: "DEV-2026-0011", annee: 2026, mois: 6, sequenceNum: 11,
    clientId: CLI2, siteId: SITE2, objet: "Rondes de sûreté multi-sites · Groupe Verlaine · 6 mois",
    clientRaisonSociale: "Groupe Verlaine", clientFormeJuridique: "SARL", clientSiret: "52487961300018",
    clientTvaIntracom: "FR62524879613", clientAdresse: "27 rue Nationale", clientCodePostal: "59000",
    clientVille: "Lille", clientPays: "France", clientContactNom: "Marc Verlaine", clientContactEmail: "direction@groupe-verlaine.fr",
    ...EMETTEUR, totalHt: 7200.0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 1440.0, totalTtc: 8640.0,
    statut: "envoye", publicToken: "demo-devtoken-verlaine-2026-0011", validiteJours: 30, dateValidite: "2026-07-12", dateEmission: "2026-06-12",
    envoyeAt: "2026-06-12T14:30:00.000Z", envoyeById: CREATOR, accepteAt: null, accepteIp: null, accepteParNom: null,
    refuseAt: null, refuseMotif: null, annuleAt: null, annuleMotif: null, contratId: null,
    notesInternes: null, conditionsParticulieres: "Facturation mensuelle à terme échu.",
    pdfStorageKey: "tenants/demo/devis/DEV-2026-0011.pdf", createdAt: "2026-06-11T10:00:00.000Z", updatedAt: "2026-06-12T14:30:00.000Z",
    lignes: [
      { id: "demo-dl-0000-0000-000000000003", devisId: "demo-dev-0000-0000-000000000002", description: "Ronde de sûreté véhiculée - tournée nocturne (3 sites)", typePeriode: "nuit", qte: 180, unite: "h", prixUnitaire: 24.0, prixHt: 4320.0, ordre: 0 },
      { id: "demo-dl-0000-0000-000000000004", devisId: "demo-dev-0000-0000-000000000002", description: "Levée de doute sur déclenchement alarme - forfait mensuel", typePeriode: null, qte: 6, unite: "forfait", prixUnitaire: 480.0, prixHt: 2880.0, ordre: 1 },
    ],
  },
  {
    id: "demo-dev-0000-0000-000000000003", numero: "DEV-2026-0008", annee: 2026, mois: 5, sequenceNum: 8,
    clientId: CLI1, siteId: SITE1, objet: "Sécurité poste fixe galerie marchande · Euralille · 12 mois",
    clientRaisonSociale: "Centre Commercial Euralille", clientFormeJuridique: "SAS", clientSiret: "39181527600025",
    clientTvaIntracom: "FR48391815276", clientAdresse: "100 Centre Commercial Euralille", clientCodePostal: "59777",
    clientVille: "Lille", clientPays: "France", clientContactNom: "Sophie Delcourt", clientContactEmail: "s.delcourt@euralille-cc.fr",
    ...EMETTEUR, totalHt: 28800.0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 5760.0, totalTtc: 34560.0,
    statut: "accepte", publicToken: "demo-devtoken-euralille-2026-0008", validiteJours: 45, dateValidite: "2026-07-04", dateEmission: "2026-05-20",
    envoyeAt: "2026-05-20T08:15:00.000Z", envoyeById: CREATOR, accepteAt: "2026-05-28T11:42:00.000Z", accepteIp: "92.184.108.12", accepteParNom: "Sophie Delcourt",
    refuseAt: null, refuseMotif: null, annuleAt: null, annuleMotif: null, contratId: CTR1,
    notesInternes: "Client historique, renouvellement annuel.",
    conditionsParticulieres: "Présence continue 7j/7 amplitude 9h-22h. Indexation annuelle indice ID.",
    pdfStorageKey: "tenants/demo/devis/DEV-2026-0008.pdf", createdAt: "2026-05-19T16:00:00.000Z", updatedAt: "2026-05-28T11:42:00.000Z",
    lignes: [
      { id: "demo-dl-0000-0000-000000000005", devisId: "demo-dev-0000-0000-000000000003", description: "Agent de sécurité ADS - poste fixe galerie (jour ouvré)", typePeriode: "jour_ouvre", qte: 1200, unite: "h", prixUnitaire: 19.5, prixHt: 23400.0, ordre: 0 },
      { id: "demo-dl-0000-0000-000000000006", devisId: "demo-dev-0000-0000-000000000003", description: "Majoration dimanche et jours fériés", typePeriode: "dimanche", qte: 240, unite: "h", prixUnitaire: 22.5, prixHt: 5400.0, ordre: 1 },
    ],
  },
  {
    id: "demo-dev-0000-0000-000000000004", numero: "DEV-2026-0006", annee: 2026, mois: 5, sequenceNum: 6,
    clientId: CLI3, siteId: SITE3, objet: "Dispositif événementiel · Foire de Lille · édition 2026",
    clientRaisonSociale: "Foire Internationale de Lille", clientFormeJuridique: "SA", clientSiret: "30217845900034",
    clientTvaIntracom: "FR91302178459", clientAdresse: "Grand Palais, Boulevard des Cités Unies", clientCodePostal: "59000",
    clientVille: "Lille", clientPays: "France", clientContactNom: "Nadia Bensaïd", clientContactEmail: "n.bensaid@foiredelille.fr",
    ...EMETTEUR, totalHt: 15400.0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 3080.0, totalTtc: 18480.0,
    statut: "refuse", publicToken: "demo-devtoken-foire-2026-0006", validiteJours: 30, dateValidite: "2026-06-09", dateEmission: "2026-05-10",
    envoyeAt: "2026-05-10T09:00:00.000Z", envoyeById: CREATOR, accepteAt: null, accepteIp: null, accepteParNom: null,
    refuseAt: "2026-05-22T17:20:00.000Z", refuseMotif: "Budget arbitré sur un autre prestataire pour cette édition.",
    annuleAt: null, annuleMotif: null, contratId: null, notesInternes: "Relancer pour l'édition 2027 dès janvier.",
    conditionsParticulieres: null, pdfStorageKey: "tenants/demo/devis/DEV-2026-0006.pdf", createdAt: "2026-05-09T11:30:00.000Z", updatedAt: "2026-05-22T17:20:00.000Z",
    lignes: [
      { id: "demo-dl-0000-0000-000000000007", devisId: "demo-dev-0000-0000-000000000004", description: "Agents de sécurité événementielle - jours d'exploitation", typePeriode: "jour_ouvre", qte: 560, unite: "h", prixUnitaire: 21.0, prixHt: 11760.0, ordre: 0 },
      { id: "demo-dl-0000-0000-000000000008", devisId: "demo-dev-0000-0000-000000000004", description: "Coordinateur sécurité (chef de poste) - forfait événement", typePeriode: null, qte: 1, unite: "forfait", prixUnitaire: 3640.0, prixHt: 3640.0, ordre: 1 },
    ],
  },
  {
    id: "demo-dev-0000-0000-000000000005", numero: "DEV-2026-0012", annee: 2026, mois: 6, sequenceNum: 12,
    clientId: CLI1, siteId: SITE1, objet: "Renfort SSIAP 1 période soldes d'été · Euralille",
    clientRaisonSociale: "Centre Commercial Euralille", clientFormeJuridique: "SAS", clientSiret: "39181527600025",
    clientTvaIntracom: "FR48391815276", clientAdresse: "100 Centre Commercial Euralille", clientCodePostal: "59777",
    clientVille: "Lille", clientPays: "France", clientContactNom: "Sophie Delcourt", clientContactEmail: "s.delcourt@euralille-cc.fr",
    ...EMETTEUR, totalHt: 4800.0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 960.0, totalTtc: 5760.0,
    statut: "envoye", publicToken: "demo-devtoken-euralille-2026-0012", validiteJours: 21, dateValidite: "2026-07-06", dateEmission: "2026-06-15",
    envoyeAt: "2026-06-15T13:05:00.000Z", envoyeById: CREATOR, accepteAt: null, accepteIp: null, accepteParNom: null,
    refuseAt: null, refuseMotif: null, annuleAt: null, annuleMotif: null, contratId: null, notesInternes: null,
    conditionsParticulieres: "Mission ponctuelle du 1er au 31 juillet 2026.",
    pdfStorageKey: "tenants/demo/devis/DEV-2026-0012.pdf", createdAt: "2026-06-15T12:40:00.000Z", updatedAt: "2026-06-15T13:05:00.000Z",
    lignes: [
      { id: "demo-dl-0000-0000-000000000009", devisId: "demo-dev-0000-0000-000000000005", description: "Agent SSIAP 1 - service sécurité incendie", typePeriode: "jour_ouvre", qte: 240, unite: "h", prixUnitaire: 20.0, prixHt: 4800.0, ordre: 0 },
    ],
  },
];

/* ---------------- Factures ---------------- */
const FACTURES = [
  {
    id: "demo-fac-0000-0000-000000000001", numero: "FAC-2026-0021", annee: 2026, mois: 5, sequenceNum: 21,
    clientId: CLI1, contratId: CTR1, clientRaisonSociale: "Centre Commercial Euralille", clientFormeJuridique: "SAS",
    clientSiret: "39181527600025", clientTvaIntracom: "FR48391815276", clientAdresse: "100 Centre Commercial Euralille",
    clientCodePostal: "59777", clientVille: "Lille", clientPays: "France", clientContactEmail: "comptabilite@euralille-cc.fr",
    ...EMETTEUR, periodeDu: "2026-05-01", periodeAu: "2026-05-31", totalHeuresFacturables: 372, totalHt: 7254.0,
    reductionFranchiseHt: 0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 1450.8, totalTtc: 8704.8,
    regleFacturation: "reel", statut: "payee", emiseAt: "2026-06-01T08:00:00.000Z", emiseById: CREATOR, dateEcheance: "2026-07-01",
    payeeAt: "2026-06-10T09:30:00.000Z", paiementReference: "VIR-20260610-EURALILLE", paiementMode: "virement",
    avoirId: null, annuleFactureId: null, annuleFactureNumero: null, motifAnnulation: null,
    paProvider: null, paMessageId: null, paStatut: null, paEnvoyeeAt: null, paAccepteeAt: null, paRefuseeAt: null, paRefusMotif: null,
    pdfStorageKey: "tenants/demo/factures/FAC-2026-0021.pdf", prevHash: null,
    hash: "a3f1c9e74b2d8056aa19f4c7e10b3d92f6c4e8a1b7d05e23c9f81a4b6d2e0f5c3",
    createdAt: "2026-05-31T16:00:00.000Z", updatedAt: "2026-06-10T09:30:00.000Z",
    lignes: [
      { id: "demo-fl-0000-0000-000000000001", factureId: "demo-fac-0000-0000-000000000001", vacationId: null, siteId: SITE1, description: "Agent ADS poste fixe - mai 2026 (jour ouvré)", typePeriode: "jour_ouvre", date: null, qte: 312, unite: "h", prixUnitaire: 19.5, prixHt: 6084.0, ordre: 0 },
      { id: "demo-fl-0000-0000-000000000002", factureId: "demo-fac-0000-0000-000000000001", vacationId: null, siteId: SITE1, description: "Majoration dimanche - mai 2026", typePeriode: "dimanche", date: null, qte: 60, unite: "h", prixUnitaire: 19.5, prixHt: 1170.0, ordre: 1 },
    ],
  },
  {
    id: "demo-fac-0000-0000-000000000002", numero: "FAC-2026-0024", annee: 2026, mois: 6, sequenceNum: 24,
    clientId: CLI2, contratId: CTR2, clientRaisonSociale: "Groupe Verlaine", clientFormeJuridique: "SARL",
    clientSiret: "52487961300018", clientTvaIntracom: "FR62524879613", clientAdresse: "27 rue Nationale",
    clientCodePostal: "59000", clientVille: "Lille", clientPays: "France", clientContactEmail: "compta@groupe-verlaine.fr",
    ...EMETTEUR, periodeDu: "2026-05-01", periodeAu: "2026-05-31", totalHeuresFacturables: 180, totalHt: 4320.0,
    reductionFranchiseHt: 0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 864.0, totalTtc: 5184.0,
    regleFacturation: "reel", statut: "emise", emiseAt: "2026-06-02T08:00:00.000Z", emiseById: CREATOR, dateEcheance: "2026-07-02",
    payeeAt: null, paiementReference: null, paiementMode: null,
    avoirId: null, annuleFactureId: null, annuleFactureNumero: null, motifAnnulation: null,
    paProvider: null, paMessageId: null, paStatut: null, paEnvoyeeAt: null, paAccepteeAt: null, paRefuseeAt: null, paRefusMotif: null,
    pdfStorageKey: "tenants/demo/factures/FAC-2026-0024.pdf", prevHash: "a3f1c9e74b2d8056aa19f4c7e10b3d92f6c4e8a1b7d05e23c9f81a4b6d2e0f5c3",
    hash: "b7e2d0a8c5149f63de24a0b9f81c7e35a6d4b2f09e17c83a5b0d6e4f2c1a9b8d7",
    createdAt: "2026-06-01T16:00:00.000Z", updatedAt: "2026-06-02T08:00:00.000Z",
    lignes: [
      { id: "demo-fl-0000-0000-000000000003", factureId: "demo-fac-0000-0000-000000000002", vacationId: null, siteId: SITE2, description: "Ronde de sûreté véhiculée - tournée nocturne mai 2026", typePeriode: "nuit", date: null, qte: 180, unite: "h", prixUnitaire: 24.0, prixHt: 4320.0, ordre: 0 },
    ],
  },
  {
    id: "demo-fac-0000-0000-000000000003", numero: "FAC-2026-0017", annee: 2026, mois: 4, sequenceNum: 17,
    clientId: CLI4, contratId: CTR4, clientRaisonSociale: "Plateau Logistique Lesquin", clientFormeJuridique: "SAS",
    clientSiret: "81034562700021", clientTvaIntracom: "FR30810345627", clientAdresse: "ZAC du Grand But, rue de l'Épinette",
    clientCodePostal: "59810", clientVille: "Lesquin", clientPays: "France", clientContactEmail: "compta@logistique-lesquin.fr",
    ...EMETTEUR, periodeDu: "2026-04-01", periodeAu: "2026-04-30", totalHeuresFacturables: 300, totalHt: 6750.0,
    reductionFranchiseHt: 0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 1350.0, totalTtc: 8100.0,
    regleFacturation: "reel", statut: "en_retard", emiseAt: "2026-05-02T08:00:00.000Z", emiseById: CREATOR, dateEcheance: "2026-06-01",
    payeeAt: null, paiementReference: null, paiementMode: null,
    avoirId: null, annuleFactureId: null, annuleFactureNumero: null, motifAnnulation: null,
    paProvider: null, paMessageId: null, paStatut: null, paEnvoyeeAt: null, paAccepteeAt: null, paRefuseeAt: null, paRefusMotif: null,
    pdfStorageKey: "tenants/demo/factures/FAC-2026-0017.pdf", prevHash: "c91a4f7e2b8d6053a0c5e9f14b7d28a3e6c0b9d5f82a17c4e0b3d6f9a2c5e8b1",
    hash: "d0b5e3a9c7261f84ef35b1c0a92d8f46b7e5c3a10f28d94b6c1e7f3a4d2b0c9e",
    createdAt: "2026-04-30T16:00:00.000Z", updatedAt: "2026-06-02T03:00:00.000Z",
    lignes: [
      { id: "demo-fl-0000-0000-000000000004", factureId: "demo-fac-0000-0000-000000000003", vacationId: null, siteId: SITE4, description: "Agent de sécurité poste fixe nuit - avril 2026", typePeriode: "nuit", date: null, qte: 300, unite: "h", prixUnitaire: 22.5, prixHt: 6750.0, ordre: 0 },
    ],
  },
  {
    id: "demo-fac-0000-0000-000000000004", numero: null, annee: null, mois: null, sequenceNum: null,
    clientId: CLI1, contratId: CTR1, clientRaisonSociale: "Centre Commercial Euralille", clientFormeJuridique: "SAS",
    clientSiret: "39181527600025", clientTvaIntracom: "FR48391815276", clientAdresse: "100 Centre Commercial Euralille",
    clientCodePostal: "59777", clientVille: "Lille", clientPays: "France", clientContactEmail: "comptabilite@euralille-cc.fr",
    emetteurNom: null, emetteurAdresse: null, emetteurSiret: null, emetteurTvaIntracom: null, emetteurMentions: null, emetteurRib: null,
    periodeDu: "2026-06-01", periodeAu: "2026-06-30", totalHeuresFacturables: 372, totalHt: 7254.0,
    reductionFranchiseHt: 0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 1450.8, totalTtc: 8704.8,
    regleFacturation: "reel", statut: "brouillon", emiseAt: null, emiseById: null, dateEcheance: null,
    payeeAt: null, paiementReference: null, paiementMode: null,
    avoirId: null, annuleFactureId: null, annuleFactureNumero: null, motifAnnulation: null,
    paProvider: null, paMessageId: null, paStatut: null, paEnvoyeeAt: null, paAccepteeAt: null, paRefuseeAt: null, paRefusMotif: null,
    pdfStorageKey: null, prevHash: null, hash: null, createdAt: "2026-06-16T16:00:00.000Z", updatedAt: "2026-06-16T16:00:00.000Z",
    lignes: [
      { id: "demo-fl-0000-0000-000000000005", factureId: "demo-fac-0000-0000-000000000004", vacationId: null, siteId: SITE1, description: "Agent ADS poste fixe - juin 2026 (jour ouvré)", typePeriode: "jour_ouvre", date: null, qte: 312, unite: "h", prixUnitaire: 19.5, prixHt: 6084.0, ordre: 0 },
      { id: "demo-fl-0000-0000-000000000006", factureId: "demo-fac-0000-0000-000000000004", vacationId: null, siteId: SITE1, description: "Majoration dimanche - juin 2026", typePeriode: "dimanche", date: null, qte: 60, unite: "h", prixUnitaire: 19.5, prixHt: 1170.0, ordre: 1 },
    ],
  },
  {
    id: "demo-fac-0000-0000-000000000005", numero: "FAC-2026-0025", annee: 2026, mois: 6, sequenceNum: 25,
    clientId: CLI4, contratId: CTR4, clientRaisonSociale: "Plateau Logistique Lesquin", clientFormeJuridique: "SAS",
    clientSiret: "81034562700021", clientTvaIntracom: "FR30810345627", clientAdresse: "ZAC du Grand But, rue de l'Épinette",
    clientCodePostal: "59810", clientVille: "Lesquin", clientPays: "France", clientContactEmail: "compta@logistique-lesquin.fr",
    ...EMETTEUR, periodeDu: "2026-05-01", periodeAu: "2026-05-31", totalHeuresFacturables: 310, totalHt: 6975.0,
    reductionFranchiseHt: 0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 1395.0, totalTtc: 8370.0,
    regleFacturation: "reel", statut: "envoyee_pa", emiseAt: "2026-06-03T08:00:00.000Z", emiseById: CREATOR, dateEcheance: "2026-07-03",
    payeeAt: null, paiementReference: null, paiementMode: null,
    avoirId: null, annuleFactureId: null, annuleFactureNumero: null, motifAnnulation: null,
    paProvider: "iopole", paMessageId: "iop-msg-7f3a9c21", paStatut: "depose", paEnvoyeeAt: "2026-06-03T08:05:00.000Z",
    paAccepteeAt: null, paRefuseeAt: null, paRefusMotif: null,
    pdfStorageKey: "tenants/demo/factures/FAC-2026-0025.pdf", prevHash: "d0b5e3a9c7261f84ef35b1c0a92d8f46b7e5c3a10f28d94b6c1e7f3a4d2b0c9e",
    hash: "e1c6f4b0d8372a95ff46c2d1ba03e957c8f6d4b21a39e05c7d2f8a5b3e4c1d0f",
    createdAt: "2026-06-02T16:00:00.000Z", updatedAt: "2026-06-03T08:05:00.000Z",
    lignes: [
      { id: "demo-fl-0000-0000-000000000007", factureId: "demo-fac-0000-0000-000000000005", vacationId: null, siteId: SITE4, description: "Agent de sécurité poste fixe nuit - mai 2026", typePeriode: "nuit", date: null, qte: 310, unite: "h", prixUnitaire: 22.5, prixHt: 6975.0, ordre: 0 },
    ],
  },
];

/* ---------------- Acomptes ---------------- */
const ACOMPTES = [
  {
    id: "demo-aco-0000-0000-000000000001", tenantId: TENANT, numero: "ACO-2026-0004", annee: 2026, mois: 5, sequenceNum: 4,
    contratId: CTR1, devisId: "demo-dev-0000-0000-000000000003", clientId: CLI1, libelle: "Acompte 30% à la signature",
    clientRaisonSociale: "Centre Commercial Euralille", clientFormeJuridique: "SAS", clientSiret: "39181527600025",
    clientTvaIntracom: "FR48391815276", clientAdresse: "100 Centre Commercial Euralille", clientCodePostal: "59777",
    clientVille: "Lille", clientPays: "France", clientContactEmail: "comptabilite@euralille-cc.fr",
    ...EMETTEUR, montantHt: 8640.0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 1728.0, totalTtc: 10368.0,
    statut: "payee", emiseAt: "2026-05-29T09:00:00.000Z", emiseById: CREATOR, dateEcheance: "2026-06-13",
    payeeAt: "2026-06-05T11:00:00.000Z", paiementReference: "VIR-20260605-EURALILLE-ACO", paiementMode: "virement",
    factureFinaleId: null, annuleAt: null, annuleMotif: null, pdfStorageKey: "tenants/demo/acomptes/ACO-2026-0004.pdf",
    createdAt: "2026-05-28T16:00:00.000Z", updatedAt: "2026-06-05T11:00:00.000Z",
  },
  {
    id: "demo-aco-0000-0000-000000000002", tenantId: TENANT, numero: "ACO-2026-0005", annee: 2026, mois: 6, sequenceNum: 5,
    contratId: CTR2, devisId: null, clientId: CLI2, libelle: "Acompte démarrage mission rondes",
    clientRaisonSociale: "Groupe Verlaine", clientFormeJuridique: "SARL", clientSiret: "52487961300018",
    clientTvaIntracom: "FR62524879613", clientAdresse: "27 rue Nationale", clientCodePostal: "59000",
    clientVille: "Lille", clientPays: "France", clientContactEmail: "compta@groupe-verlaine.fr",
    ...EMETTEUR, montantHt: 2160.0, tvaPct: 20, autoliquidationTva: false, tvaMontant: 432.0, totalTtc: 2592.0,
    statut: "emise", emiseAt: "2026-06-10T09:00:00.000Z", emiseById: CREATOR, dateEcheance: "2026-06-25",
    payeeAt: null, paiementReference: null, paiementMode: null, factureFinaleId: null, annuleAt: null, annuleMotif: null,
    pdfStorageKey: "tenants/demo/acomptes/ACO-2026-0005.pdf", createdAt: "2026-06-09T16:00:00.000Z", updatedAt: "2026-06-10T09:00:00.000Z",
  },
];

/* ---------------- Trésorerie ---------------- */
const TRESORERIE = {
  asOf: "2026-06-18T00:00:00.000Z",
  encours: { count: 2, totalHt: 11295.0, totalTtc: 13554.0 },
  enRetard: { count: 1, totalHt: 6750.0, totalTtc: 8100.0 },
  aVenir60j: { count: 2, totalHt: 11295.0, totalTtc: 13554.0 },
  payeesMois: { count: 1, totalHt: 7254.0, totalTtc: 8704.8 },
  annulees: { count: 0, totalHt: 0, totalTtc: 0 },
};

/* ---------------- Helpers de filtrage ---------------- */
function find(arr: any[], id: string) {
  return arr.find((x) => x.id === id);
}
function byStatut(arr: any[], q: URLSearchParams) {
  const s = q.get("statut");
  return s ? arr.filter((x) => x.statut === s) : arr;
}
function byStatutClient(arr: any[], q: URLSearchParams) {
  let out = byStatut(arr, q);
  const c = q.get("clientId");
  if (c) out = out.filter((x) => x.clientId === c);
  return out;
}
function filterSites(arr: any[], q: URLSearchParams) {
  let out = arr;
  const s = q.get("statut");
  const t = q.get("type");
  if (s) out = out.filter((x) => x.statut === s);
  if (t) out = out.filter((x) => x.type === t);
  return out;
}
function filterContrats(arr: any[], q: URLSearchParams) {
  let out = byStatut(arr, q);
  const c = q.get("clientId");
  const si = q.get("siteId");
  if (c) out = out.filter((x) => x.clientId === c);
  if (si) out = out.filter((x) => x.siteId === si);
  return out;
}
function overlapRange(
  arr: any[],
  q: URLSearchParams,
  startKey: string,
  endKey: string,
) {
  const from = q.get("from") ?? q.get("debut");
  const to = q.get("to") ?? q.get("fin");
  if (!from && !to) return arr;
  return arr.filter((x) => {
    const start = x[startKey];
    const end = x[endKey] ?? x[startKey];
    if (from && end < from) return false;
    if (to && start > to) return false;
    return true;
  });
}
function filterPointages(arr: any[], q: URLSearchParams) {
  const vacId = q.get("vacationId");
  if (vacId) return arr.filter((x) => x.vacationId === vacId);
  const ag = q.get("agentId");
  let out = arr;
  if (ag) out = out.filter((x) => x.agentId === ag);
  return overlapRange(out, q, "pointeeAt", "pointeeAt");
}

/**
 * Renvoie la donnée fictive pour un GET, ou `undefined` si aucune fixture
 * ne correspond (→ requête réelle laissée passer).
 */
export function matchFixture(pathWithQuery: string): unknown {
  const [rawPath, rawQuery = ""] = pathWithQuery.split("?");
  const path = rawPath ?? pathWithQuery;
  const q = new URLSearchParams(rawQuery);
  const extra = matchExtra(path, q);
  if (extra !== undefined) return extra;

  // Listes
  if (path === "/v1/etablissements") return ETABLISSEMENTS;
  if (path === "/v1/sites") return filterSites(SITES, q);
  if (path === "/v1/clients") return byStatut(CLIENTS, q);
  if (path === "/v1/agents") return byStatut(AGENTS, q);
  if (path === "/v1/contrats") return filterContrats(CONTRATS, q);
  if (path === "/v1/vacations") return overlapRange(VACATIONS, q, "debut", "fin");
  if (path === "/v1/pointages") return filterPointages(POINTAGES, q);
  if (path === "/v1/devis") return byStatutClient(DEVIS, q);
  if (path === "/v1/factures/treasury") return TRESORERIE;
  if (path === "/v1/factures") return byStatutClient(FACTURES, q);
  if (path === "/v1/factures-acompte") return byStatut(ACOMPTES, q);

  // Détails (:id) & sous-ressources
  let m: RegExpMatchArray | null;
  if ((m = path.match(/^\/v1\/agents\/([^/]+)\/qualifications$/)) && m[1])
    return QUALIFS[m[1]] ?? [];
  if ((m = path.match(/^\/v1\/agents\/([^/]+)\/rib$/)) && m[1])
    return RIBS[m[1]] ?? null;
  if ((m = path.match(/^\/v1\/agents\/([^/]+)$/)) && m[1]) return find(AGENTS, m[1]);
  if ((m = path.match(/^\/v1\/clients\/([^/]+)$/)) && m[1]) return find(CLIENTS, m[1]);
  if ((m = path.match(/^\/v1\/contrats\/([^/]+)$/)) && m[1]) return find(CONTRATS, m[1]);
  if ((m = path.match(/^\/v1\/sites\/([^/]+)$/)) && m[1]) return find(SITES, m[1]);
  if ((m = path.match(/^\/v1\/etablissements\/([^/]+)$/)) && m[1]) return find(ETABLISSEMENTS, m[1]);
  if ((m = path.match(/^\/v1\/vacations\/([^/]+)$/)) && m[1]) return find(VACATIONS, m[1]);
  if ((m = path.match(/^\/v1\/devis\/([^/]+)$/)) && m[1]) return find(DEVIS, m[1]);
  if ((m = path.match(/^\/v1\/factures\/([^/]+)$/)) && m[1]) return find(FACTURES, m[1]);
  if ((m = path.match(/^\/v1\/factures-acompte\/([^/]+)$/)) && m[1]) return find(ACOMPTES, m[1]);

  return undefined;
}
